import type { Localized } from './releaseNotes'

export interface TutorialMedia {
  type: 'image' | 'video'
  /** Image URL, or poster image for a video. */
  src: string
  /** Video file URL — when missing, the video shows as "coming soon". */
  videoSrc?: string
  duration?: string
  caption: Localized
}

/** One step of a tutorial: a short explanation, optionally illustrated. */
export interface TutorialStep {
  title: Localized
  text: Localized
  media?: TutorialMedia
}

export interface ReleaseTutorial {
  intro: Localized
  /** Overview video shown before the steps. */
  video?: TutorialMedia
  steps: TutorialStep[]
}

const IMG = {
  briefing: 'https://cdn.magicpatterns.com/patterns/generated-images/385a7875-8ff7-420d-862c-19ae156ed0b4.jpg',
  slaMap: 'https://cdn.magicpatterns.com/patterns/generated-images/fd1fab8c-a0a3-4a73-8b6a-f366c19da09a.jpg',
  audience: 'https://cdn.magicpatterns.com/patterns/generated-images/f64ca061-5659-4479-8dcc-0de6faaa4065.jpg',
  appChannel: 'https://cdn.magicpatterns.com/patterns/generated-images/d1f0a5e2-ed7d-4c6e-b3df-eb72e73f48cf.jpg',
}

/** Step-by-step tutorials shown in the side panel, keyed by "<version>:<change index>". */
export const releaseTutorials: Record<string, ReleaseTutorial> = {
  '4.8.0:0': {
    intro: {
      fr: 'Le briefing territorial résume en quelques cartes ce qui change sur votre territoire. ATNA regroupe les requêtes liées à une même situation et distingue les faits, les explications documentées et les suggestions.',
      en: 'The territorial briefing sums up in a few cards what is changing in your territory. ATNA groups requests tied to the same situation and separates facts, documented explanations and suggestions.',
    },
    video: {
      type: 'video',
      src: IMG.briefing,
      duration: '1:20',
      caption: { fr: 'Découvrir le briefing territorial', en: 'Discover the territorial briefing' },
    },
    steps: [
      {
        title: { fr: 'Ouvrez le tableau de bord', en: 'Open the dashboard' },
        text: {
          fr: 'Dans Requêtes, ouvrez l’onglet Analytiques. Le briefing se trouve sous la carte. Il couvre votre district si vous êtes conseiller, ou toute la ville pour la mairie et la direction générale.',
          en: 'In Requests, open the Analytics tab. The briefing sits below the map. It covers your district if you are a councillor, or the whole city for the mayor and the CAO.',
        },
      },
      {
        title: { fr: 'Repérez ce qui a changé', en: 'Spot what changed' },
        text: {
          fr: 'En haut, « Depuis votre dernière visite » compte les nouveaux enjeux, les situations aggravées et les problèmes résolus.',
          en: 'At the top, “Since your last visit” counts new issues, situations that got worse and problems resolved.',
        },
      },
      {
        title: { fr: 'Lisez les cartes', en: 'Read the cards' },
        text: {
          fr: 'Chaque carte a un badge (Important, En hausse, À anticiper, À partager…), un titre concret et un résumé factuel. « Voir le détail » affiche pourquoi l’enjeu mérite votre attention et l’explication documentée.',
          en: 'Each card has a badge (Important, Rising, To anticipate, To share…), a concrete title and a factual summary. “See details” shows why the issue deserves your attention and the documented explanation.',
        },
        media: { type: 'image', src: IMG.briefing, caption: { fr: 'Les cartes du briefing', en: 'Briefing cards' } },
      },
      {
        title: { fr: 'Suivez les engagements', en: 'Track commitments' },
        text: {
          fr: 'Quand un service a confirmé une date, l’encart « Engagement à suivre » montre l’échéance et l’avancement.',
          en: 'When a department has confirmed a date, the “Commitment to follow” box shows the due date and progress.',
        },
      },
    ],
  },
  '4.8.0:1': {
    intro: {
      fr: 'Chaque enjeu du briefing propose une action rapide, préparée par ATNA.',
      en: 'Each briefing issue offers a quick action, prepared by ATNA.',
    },
    video: {
      type: 'video',
      src: IMG.briefing,
      duration: '0:40',
      caption: { fr: 'Préparer une action rapide', en: 'Preparing a quick action' },
    },
    steps: [
      {
        title: { fr: 'Cliquez sur l’action de la carte', en: 'Click the card’s action' },
        text: {
          fr: '« Demande de suivi » pour un enjeu à éclaircir, « Information citoyenne » pour un résultat à partager ou un sujet à anticiper.',
          en: '“Follow-up request” for an issue to clarify, “Citizen information” for a result to share or a topic to anticipate.',
        },
        media: { type: 'image', src: IMG.briefing, caption: { fr: 'Action rapide sur une carte', en: 'Quick action on a card' } },
      },
      {
        title: { fr: 'Relisez le brouillon', en: 'Review the draft' },
        text: {
          fr: 'ATNA remplit le destinataire, l’objet et le message à partir des données de la situation. Modifiez le texte au besoin.',
          en: 'ATNA fills in the recipient, subject and message from the situation’s data. Edit the text as needed.',
        },
      },
      {
        title: { fr: 'Envoyez', en: 'Send' },
        text: {
          fr: 'Une fois envoyé, le bouton indique « Demande envoyée » ou « Transmise aux Communications ».',
          en: 'Once sent, the button reads “Request sent” or “Sent to Communications”.',
        },
      },
    ],
  },
  '4.7.0:0': {
    intro: {
      fr: 'La carte affiche les districts et les secteurs de Brossard, colorés selon le respect des SLA.',
      en: 'The map shows Brossard’s districts and sectors, coloured by SLA compliance.',
    },
    video: { type: 'video', src: IMG.slaMap, duration: '0:55', caption: { fr: 'Naviguer dans la carte', en: 'Navigating the map' } },
    steps: [
      {
        title: { fr: 'Choisissez la vue', en: 'Choose the view' },
        text: {
          fr: 'En haut de la carte, la bascule « Par : District / Secteur » change le découpage affiché.',
          en: 'At the top of the map, the “By: District / Sector” switch changes the boundaries shown.',
        },
        media: { type: 'image', src: IMG.slaMap, caption: { fr: 'Carte des districts', en: 'District map' } },
      },
      {
        title: { fr: 'Sélectionnez un territoire', en: 'Select an area' },
        text: {
          fr: 'Cliquez sur un district ou choisissez-le dans la liste de droite. L’icône cible revient à toute la ville. Le tableau « Performance par service » suit votre sélection.',
          en: 'Click a district or pick it from the list on the right. The target icon goes back to the whole city. The “Performance by service” table follows your selection.',
        },
      },
      {
        title: { fr: 'Ajustez la période et la taille', en: 'Adjust period and size' },
        text: {
          fr: 'Le calendrier change la période (24H, 7J, 30J, 3M, année). L’icône d’agrandissement affiche la carte en grand.',
          en: 'The calendar changes the period (24H, 7D, 30D, 3M, year). The expand icon shows the map full width.',
        },
      },
    ],
  },
  '4.7.0:1': {
    intro: {
      fr: 'Le mode Détails place chaque requête sur la carte, colorée selon son SLA.',
      en: 'Details mode places each request on the map, coloured by its SLA.',
    },
    video: { type: 'video', src: IMG.slaMap, duration: '0:50', caption: { fr: 'Explorer les requêtes sur la carte', en: 'Exploring requests on the map' } },
    steps: [
      {
        title: { fr: 'Activez « Détails »', en: 'Turn on “Details”' },
        text: {
          fr: 'Les épingles apparaissent en vert (SLA respecté), orange (proche de l’échéance) ou rouge (dépassé).',
          en: 'Pins appear in green (SLA met), orange (close to deadline) or red (missed).',
        },
        media: { type: 'image', src: IMG.slaMap, caption: { fr: 'Épingles des requêtes', en: 'Request pins' } },
      },
      {
        title: { fr: 'Filtrez par état', en: 'Filter by state' },
        text: {
          fr: 'Cliquez sur un compteur coloré pour n’afficher que les requêtes de cet état. Cliquez à nouveau pour retirer le filtre.',
          en: 'Click a coloured counter to show only requests in that state. Click again to clear the filter.',
        },
      },
      {
        title: { fr: 'Ouvrez une requête', en: 'Open a request' },
        text: {
          fr: 'Un clic sur une épingle affiche sa fiche à droite : la frise du délai comparé au SLA et un résumé ATNA qui explique la situation.',
          en: 'Clicking a pin shows its card on the right: the deadline bar against the SLA and an ATNA summary explaining the situation.',
        },
      },
    ],
  },
  '4.5.0:0': {
    intro: {
      fr: 'Le canal Application publie l’avis dans le fil de nouvelles de l’app citoyenne.',
      en: 'The App channel publishes the notice in the citizen app news feed.',
    },
    video: { type: 'video', src: IMG.appChannel, duration: '0:45', caption: { fr: 'Publier dans l’application', en: 'Publishing to the app' } },
    steps: [
      {
        title: { fr: 'Activez le canal', en: 'Turn on the channel' },
        text: {
          fr: 'Dans « Créer un avis », ouvrez la carte Application parmi les canaux de diffusion.',
          en: 'In “Create a notice”, open the App card among the delivery channels.',
        },
      },
      {
        title: { fr: 'Rédigez titre, résumé et contenu', en: 'Write title, summary and content' },
        text: {
          fr: 'Le résumé (160 caractères) sert d’aperçu dans le fil. Le contenu complet s’affiche quand le citoyen ouvre l’avis.',
          en: 'The summary (160 characters) is the feed preview. The full content shows when the citizen opens the notice.',
        },
        media: { type: 'image', src: IMG.appChannel, caption: { fr: 'Aperçu dans l’app citoyenne', en: 'Citizen app preview' } },
      },
      {
        title: { fr: 'Épinglez si nécessaire', en: 'Pin if needed' },
        text: {
          fr: 'Cochez « Épingler en haut du fil » pour qu’un avis important reste au-dessus des autres jusqu’à sa date de fin.',
          en: 'Tick “Pin to the top of the feed” so an important notice stays above the others until its end date.',
        },
      },
    ],
  },
  '4.5.0:2': {
    intro: {
      fr: 'Le bloc Audience suit la logique « Où × Qui » : les lieux s’additionnent, les critères différents se combinent.',
      en: 'The Audience block follows a “Where × Who” logic: places add up, different criteria combine.',
    },
    video: { type: 'video', src: IMG.audience, duration: '1:05', caption: { fr: 'Cibler son audience', en: 'Targeting your audience' } },
    steps: [
      {
        title: { fr: 'Choisissez « Où »', en: 'Choose “Where”' },
        text: {
          fr: 'Toute la ville, ou des lieux précis : secteurs, adresses exactes ou zones dessinées sur la carte. Ajoutez-en autant que nécessaire.',
          en: 'The whole city, or specific places: sectors, exact addresses or zones drawn on the map. Add as many as needed.',
        },
        media: { type: 'image', src: IMG.audience, caption: { fr: 'Le bloc Audience', en: 'The Audience block' } },
      },
      {
        title: { fr: 'Ajoutez des critères « Qui »', en: 'Add “Who” criteria' },
        text: {
          fr: '« + Ajouter un critère » : tranche d’âge, détenteurs de cartes, résidence ou équipe interne. Plusieurs valeurs d’un même critère = l’une ou l’autre.',
          en: '“+ Add a criterion”: age bracket, card holders, residence or internal team. Several values of one criterion = either one.',
        },
      },
      {
        title: { fr: 'Vérifiez le résumé', en: 'Check the summary' },
        text: {
          fr: 'En bas du bloc, la formule complète et le nombre de personnes ciblées. Chaque filtre se retire d’un clic.',
          en: 'At the bottom of the block, the full formula and the number of people targeted. Each filter can be removed in one click.',
        },
      },
    ],
  },
}
